import undoRedoManager from './UndoRedoManager';
import store from '../redux/store';
import {
  captureGridHistoryState,
  getGridHistoryScene,
  gridHistorySnapshotsEqual,
} from './gridHistory';
import {
  isGridHistoryReady,
  isGridHistorySuppressed,
} from './gridHistorySuppress';
import {
  cancelPendingGridHistoryCommit,
  clearPendingGridHistoryBefore,
  getPendingGridHistoryBefore,
  getGridHistoryCommitTimer,
  GRID_HISTORY_COMMIT_DEBOUNCE_MS,
  isPendingGridHistorySessionCurrent,
  setGridHistoryCommitTimer,
  setPendingGridHistoryBefore,
} from './gridHistoryCommit';
import { getLastCommittedGridHistory } from './gridHistoryBaseline';
import { flushPendingGridHistoryCommit } from './gridHistoryFlush';

const RESTORE_ACTION_TYPES = new Set([
  'data/restoreGridHistorySnapshot',
  'particles/setParticlesHistory',
  'animation/setAnimationObjectsHistory',
  'environment/restoreEnvironmentHistory',
  'physics/restorePhysicsHistory',
]);

/** All user-facing editor mutations that should create undo steps. */
const RECORD_ACTION_TYPES = new Set([
  'data/update_assets_data',
  'data/update_assets_data_new',
  'data/lightDataUpdate',
  'data/updateLight',
  'data/updateCameraType',
  'data/cameraPropertiesUpdate',
  'data/updateAssetVisibility',
  'data/addEvent',
  'data/removeEvent',
  'data/clearEvents',
  'data/addLight',
  'data/removeLight',
  'data/addCamera',
  'data/removeCamera',
  'data/addSceneMesh',
  'data/addSceneHotspot',
  'data/addSceneImage',
  'data/addSceneVideo',
  'data/addSceneAudio',
  'data/addSceneText',
  'data/addSceneElement',
  'data/removeSceneMesh',
  'data/removeSceneHotspot',
  'data/removeSceneVideo',
  'data/remove_assets_by_id',
  'data/setSceneMeshes',
  'data/addAsset',
  'add_assets/fulfilled',
  'remove_assets_by_id/fulfilled',
  'particles/updateParticle',
  'particles/addParticle',
  'particles/removeParticle',
  'particles/playParticle',
  'particles/pauseParticle',
  'particles/stopParticle',
  'animation/updateAnimationObject',
  'animation/addAnimationObject',
  'animation/removeAnimationObject',
  'animation/cleanAllAnimationObjects',
  'environment/setEnvironmentData',
  'physics/setObjectPhysicsProperties',
  'physics/removeObjectPhysics',
  'physics/updateWorldSettings',
  'physics/setPhysicsEnabled',
  'physics/updateDefaultPhysicsProperties',
]);

/** One undo step per action — no debounce batching. */
const IMMEDIATE_RECORD_ACTION_TYPES = new Set([
  'data/addEvent',
  'data/removeEvent',
  'data/clearEvents',
  'data/addLight',
  'data/removeLight',
  'data/addCamera',
  'data/removeCamera',
  'data/addSceneMesh',
  'data/addSceneHotspot',
  'data/addSceneImage',
  'data/addSceneVideo',
  'data/addSceneAudio',
  'data/addSceneText',
  'data/addSceneElement',
  'data/removeSceneMesh',
  'data/removeSceneHotspot',
  'data/removeSceneVideo',
  'data/remove_assets_by_id',
  'data/setSceneMeshes',
  'data/addAsset',
  'data/updateAssetVisibility',
  'add_assets/fulfilled',
  'remove_assets_by_id/fulfilled',
  'particles/addParticle',
  'particles/removeParticle',
  'animation/addAnimationObject',
  'animation/removeAnimationObject',
  'animation/cleanAllAnimationObjects',
  'physics/removeObjectPhysics',
  'physics/setPhysicsEnabled',
]);

export { suppressGridHistory, isGridHistorySuppressed } from './gridHistorySuppress';
export { cancelPendingGridHistoryCommit } from './gridHistoryCommit';
export { flushPendingGridHistoryCommit } from './gridHistoryFlush';
export {
  beginSuppressedMeshAddHistory,
  captureDiscreteHistoryBefore,
  recordDiscreteHistoryAfter,
  runDiscreteGridHistoryStep,
} from './gridHistoryDiscrete';

function shouldRecordAction(action) {
  if (!action?.type) return false;
  if (RESTORE_ACTION_TYPES.has(action.type)) return false;
  if (!RECORD_ACTION_TYPES.has(action.type)) return false;
  if (!isGridHistoryReady()) return false;
  if (isGridHistorySuppressed()) return false;

  const state = store.getState();
  if (state?.window?.window_type === 'preview') return false;
  if (state?.global_state?.isPublishing) return false;
  if (state?.global_state?.loading) return false;
  if (state?.assets_object?.assets_loading) return false;

  return true;
}

function resolveBeforeSnapshot() {
  return getPendingGridHistoryBefore() || getLastCommittedGridHistory();
}

function commitPendingNow(label) {
  flushPendingGridHistoryCommit(
    (before, scene, commitLabel) =>
      undoRedoManager.recordGridHistoryTransitionCore(before, scene, commitLabel),
    label
  );
}

function scheduleCommit(label) {
  const existing = getGridHistoryCommitTimer();
  if (existing) clearTimeout(existing);

  const timer = setTimeout(() => {
    setGridHistoryCommitTimer(null);
    commitPendingNow(label);
  }, GRID_HISTORY_COMMIT_DEBOUNCE_MS);

  setGridHistoryCommitTimer(timer);
}

export function beginGridHistoryEdit() {
  if (!isGridHistoryReady() || isGridHistorySuppressed()) return;
  const scene = getGridHistoryScene();
  const baseline = getLastCommittedGridHistory();
  if (!scene || !baseline || getPendingGridHistoryBefore()) return;
  setPendingGridHistoryBefore(baseline);
}

export function commitGridHistoryEdit(label = 'edit') {
  if (isGridHistorySuppressed() || !getPendingGridHistoryBefore()) return;
  scheduleCommit(label);
}

export function flushGridHistoryEdit(label = 'edit') {
  if (getPendingGridHistoryBefore()) {
    commitPendingNow(label);
    return;
  }

  const scene = getGridHistoryScene();
  const before = getLastCommittedGridHistory();
  if (!before || !scene) return;
  if (!isGridHistoryReady() || isGridHistorySuppressed()) return;

  const after = captureGridHistoryState(scene);
  if (!gridHistorySnapshotsEqual(before, after)) {
    undoRedoManager.recordGridHistoryTransitionCore(before, scene, label);
  }
}

const gridHistoryMiddleware = () => (next) => (action) => {
  const record = shouldRecordAction(action);
  const actionLabel = action.type?.split('/').pop() || 'edit';

  if (record && !getPendingGridHistoryBefore()) {
    const before = resolveBeforeSnapshot();
    if (before) setPendingGridHistoryBefore(before);
  }

  const result = next(action);

  if (record) {
    if (IMMEDIATE_RECORD_ACTION_TYPES.has(action.type)) {
      commitPendingNow(actionLabel);
    } else {
      scheduleCommit(actionLabel);
    }
  }

  return result;
};

export default gridHistoryMiddleware;
